import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { gameTypes, isContestGamesWithSecondaryUgc, isUgcGames } from 'config/promoGameTypes';
import { getEntryVerifyStatus } from 'config/entryVerifyStatus';
import { isJSONParsable, isObject } from 'utils/objectManipulate';
import { utcDTStr2LocalDTStr } from 'utils/dateTime';
import { componentStylePropTypes } from 'client/gameFormBased/utils/propTypes';
import { css } from '@emotion/core';
import { below } from 'utils/breakpoint';
import ResultInfoInvoice from 'client/components/Result/EntriesInfo/customResult/ResultInfoInvoice';
import ResultUGC from 'client/components/Result/EntriesInfo/customResult/ResultUGC';
import ResultContest from 'client/components/Result/EntriesInfo/customResult/ResultContest';
import ResultComment from 'client/components/Result/EntriesInfo/customResult/ResultComment';
import ResultInfoList from 'client/components/Result/EntriesInfo/customResult/ResultInfoList';

function CustomResultList({
    items = [],
    gameType,
    componentStyle,
    showFullList = false,
    hasCustomResult
}) {
    const { enableVerifyMechanism } = useSelector(s => s.settingPromo);
    const { id: signUpFormId } = useSelector(s => s.signUpForm);
    const isShowValidStatus = Boolean(enableVerifyMechanism);
    const { Comp = () => false } = getCustomResultByGameType(gameType, isShowValidStatus);
    const parseData = data => (isObject(data) ? data : JSON.parse(data));

    const sortedItems = useMemo(() => {
        if (gameType === gameTypes.PURCHASE_REWARD) {
            return [...items].sort((a, b) => {
                const dateA = new Date(a.flow?.gameResult);
                const dateB = new Date(b.flow?.gameResult);
                return dateB - dateA;
            });
        }
        return items;
    }, [items, gameType]);

    return (
        <div
            css={[
                containerStyle,
                css(
                    `color: ${componentStyle.titleColor};`,
                    showFullList && !hasCustomResult ? 'min-height: 320px;' : ''
                )
            ]}
        >
            {sortedItems.map(item => {
                const {
                    flow: { gameResult: gameResultCreatedAt, formSignUp, qualified = false } = {},
                    deletedAt,
                    gameResult,
                    id,
                    uuid: entryUuid
                } = item;
                const validStatus = getEntryVerifyStatus({ deletedAt, qualified });
                const resultCreatedAt = Boolean(gameResultCreatedAt)
                    ? dayjs(utcDTStr2LocalDTStr(gameResultCreatedAt)).format('YYYY-MM-DD HH:mm')
                    : 'YYYY-MM-DD HH:mm';
                const result = isJSONParsable(gameResult)
                    ? parseData(gameResult)
                    : gameResult ?? {};

                const showFormBtn = Boolean(formSignUp);
                return (
                    <div key={id}>
                        <Comp
                            {...{
                                isMultiple: items.length > 1,
                                serialNo: id,
                                resultCreatedAt,
                                componentStyle,
                                validStatus,
                                resultInfo: result,
                                gameType,
                                entryUuid,
                                formIds: {
                                    signUpFormId
                                },
                                showFormBtn
                            }}
                        />
                    </div>
                );
            })}
        </div>
    );
}

CustomResultList.propTypes = {
    items: PropTypes.array,
    gameType: PropTypes.number,
    componentStyle: componentStylePropTypes
};

export default CustomResultList;

export function getCustomResultByGameType(gameType) {
    switch (true) {
        case gameType === gameTypes.PURCHASE_REWARD:
        case gameType === gameTypes.ORDER_REWARD:
            return { Comp: ResultInfoList };
        case gameType === gameTypes.INVOICE:
            return { Comp: ResultInfoInvoice };
        case gameType === gameTypes.COMMENT:
            return { Comp: ResultComment };
        case isContestGamesWithSecondaryUgc(gameType):
            return { Comp: ResultContest };
        case isUgcGames(gameType):
            return { Comp: ResultUGC };
        default:
            return {};
    }
}

const containerStyle = css`
    padding: 0px 24px;
    ${below('sm')} {
        padding: 8px 16px;
    }
`;
